import { dataTrigger as attr, track as send } from "@bk/app-events";

/**
 * Stable app-events trigger catalog. Old manual analytics names are included
 * in descriptions where an existing counter was migrated.
 */
export const TRIGGERS = {
  "ref.app.open": "App opening. Replaces old mini_app_session_start.",

  "ref.main.balance": "Open balance. Old: main_click_balance.",
  "ref.main.possible_income_help":
    "Open possible-income help. Old: main_click_possible_income_help.",
  "ref.main.conditions":
    "Open referral-program conditions. Old: main_open_referral_conditions.",
  "ref.main.multiplier":
    "Open referral multiplier. Old: main_open_referral_multiplier_banner.",
  "ref.main.back_to_kef": "Return from the mini-app to kef.",

  "ref.balance.invite_more":
    "Invite another referral from balance. Old: balance_submenu_invite_more.",
  "ref.balance.withdraw": "Withdraw balance. Old: balance_submenu_withdraw.",
  "ref.balance.close": "Close balance dialog.",
  "ref.balance.success_close": "Close successful-withdrawal dialog.",
  "ref.income_help.close": "Close possible-income help dialog.",

  "ref.invite.copy_delivery":
    "Copy Delivery invite link. Old: main_copy_link_delivery.",
  "ref.invite.copy_taxi": "Copy Taxi invite link. Old: main_copy_link_taxi.",
  "ref.invite.copy_eats": "Copy Eats invite link. Old: main_copy_link_eats.",
  "ref.invite.share_delivery":
    "Share Delivery invite. Old: main_invite_delivery.",
  "ref.invite.share_taxi": "Share Taxi invite. Old: main_invite_taxi.",
  "ref.invite.share_eats": "Share Eats invite. Old: main_invite_eats.",
  "ref.invite.qr": "Open invite QR code.",

  "ref.conditions.back": "Return from the conditions list.",
  "ref.conditions.program_delivery":
    "Open a Delivery program. Old: conditions_click_program_delivery.",
  "ref.conditions.program_taxi":
    "Open a Taxi program. Old: conditions_click_program_taxi.",
  "ref.conditions.program_eats":
    "Open an Eats program. Old: conditions_click_program_eats.",
  "ref.conditions.detail_back": "Return from a program detail page.",
  "ref.conditions.copy_delivery":
    "Copy Delivery invite on details. Old: condition_detail_copy_link_delivery.",
  "ref.conditions.copy_taxi":
    "Copy Taxi invite on details. Old: condition_detail_copy_link_taxi.",
  "ref.conditions.copy_eats":
    "Copy Eats invite on details. Old: condition_detail_copy_link_eats.",
  "ref.conditions.share_delivery":
    "Share Delivery invite on details. Old: condition_detail_invite_delivery.",
  "ref.conditions.share_taxi":
    "Share Taxi invite on details. Old: condition_detail_invite_taxi.",
  "ref.conditions.share_eats":
    "Share Eats invite on details. Old: condition_detail_invite_eats.",

  "ref.referrals.completed":
    "Toggle completed referrals. Old: referrals_tab_completed.",
  "ref.referrals.unregistered":
    "Toggle unregistered referrals. Old: referrals_tab_unregistered.",
  "ref.referrals.missed": "Toggle missed referrals. Old: referrals_tab_missed.",
  "ref.referrals.username":
    "Open referral contact. Old: referrals_click_referral_username.",
  "ref.referrals.remind_registration":
    "Remind a referral to register. Old: referrals_native_followup_remind_registration.",
  "ref.referrals.remind_goal":
    "Remind a referral with progress. Old: referrals_native_followup_remind_one_goal.",
  "ref.referrals.support_goal":
    "Help a referral without progress. Old: referrals_native_followup_support_zero_goals.",

  "ref.sort.open": "Open sorting. Old: main_open_sort_screen.",
  "ref.sort.max_benefit":
    "Sort by maximum benefit. Old: sort_screen_max_benefit.",
  "ref.sort.min_benefit":
    "Sort by minimum benefit. Old: sort_screen_min_benefit.",
  "ref.sort.date_desc": "Sort newest first. Old: sort_screen_date_desc.",
  "ref.sort.date_asc": "Sort oldest first. Old: sort_screen_date_asc.",

  "ref.multiplier.referral_username":
    "Open referral from multiplier. Old: referral_multiplier_click_referral_username.",
  "ref.multiplier.invite_friend":
    "Invite from multiplier. Old: referral_multiplier_invite_friend.",
  "ref.multiplier.back": "Return from the multiplier page.",
  "ref.multiplier.load_more": "Load more multiplier referrals.",
  "ref.multiplier.celebration_close": "Close multiplier celebration dialog.",

  "ref.pagination.previous": "Go to the previous list page.",
  "ref.pagination.page": "Select a list page.",
  "ref.pagination.next": "Go to the next list page.",
  "ref.qr.share": "Share the invite QR card.",
  "ref.qr.close": "Close the invite QR dialog.",
  "ref.registration.open_bot": "Open the registration bot.",
  "ref.instructions.toggle": "Expand or collapse referral instructions.",

  "ref.main.tab_invite": "Open the invite tab.",
  "ref.main.tab_statistics": "Open the statistics tab.",
  "ref.period.open": "Open statistics period selection.",
  "ref.period.preset": "Select a statistics period preset.",
  "ref.period.custom": "Open custom period calendar.",
  "ref.period.mode_registration": "Switch statistics to registration mode.",
  "ref.period.mode_completion": "Switch statistics to completion mode.",
  "ref.period.previous_month": "Show the previous calendar month.",
  "ref.period.next_month": "Show the next calendar month.",
  "ref.period.day": "Select a calendar day.",
  "ref.period.apply": "Apply a custom period.",
  "ref.period.reset": "Reset or leave custom period selection.",
  "ref.tariff.open": "Open tariff selection.",
  "ref.tariff.toggle_all": "Select or clear every tariff.",
  "ref.tariff.option": "Toggle a tariff.",
  "ref.tariff.apply": "Apply selected tariffs.",

  "ref.funnel.registered": "Toggle registered-referrals funnel step.",
  "ref.funnel.orders_5": "Toggle five-orders funnel step.",
  "ref.funnel.orders_30": "Toggle thirty-orders funnel step.",
  "ref.funnel.referral": "Open referral details from the funnel.",
  "ref.referral_details.message": "Message a referral from details.",
  "ref.referral_details.back": "Close referral details.",

  "ref.contest.open": "Open blogger contest leaderboard.",
  "ref.contest.back": "Return from blogger contest.",
  "ref.contest.views": "Switch contest rating to views.",
  "ref.contest.registrations": "Switch contest rating to registrations.",
  "ref.contest.my_position": "Scroll to the user's contest position.",
} as const;

export type Trigger = keyof typeof TRIGGERS;
export const dataTrigger = (name: Trigger) => attr(name);
export const track = (name: Trigger) => send(name);
